import { useSyncExternalStore } from 'react';
import { onConsentChange } from '@fff-web/consent';
import { ALLOWED_STATES, COUNTRY_SHORTHANDS, type AllowedState } from '@fff-web/constants';

declare global {
  interface Window {
    OneTrust: unknown;
  }
}

/** OneTrust populates its geolocation shortly after the stub script loads. */
const POLL_INTERVAL_MS = 500;
const POLL_TIMEOUT_MS = 5000;

/**
 * OneTrust also fills `state` outside the US with the local region code (e.g. Brazil's `MT`),
 * which would collide with the opt-out states.
 */
const readGeoState = (): string => {
  try {
    const geolocation = window?.OneTrust?.getGeolocationData();
    const isUnitedStates = geolocation?.country?.toUpperCase() === COUNTRY_SHORTHANDS.UNITED_STATES;
    return isUnitedStates ? geolocation.state || '' : '';
  } catch {
    return '';
  }
};

const subscribeGeoState = (onStoreChange: () => void): (() => void) => {
  if (typeof window === 'undefined' || readGeoState()) return () => undefined;

  const resolve = () => {
    if (!readGeoState()) return;
    onStoreChange();
    cleanup();
  };

  const interval = setInterval(resolve, POLL_INTERVAL_MS);

  // Stop polling once OneTrust's expected load window elapses. A blocked, failed, or
  // stateless (non-US) geolocation would otherwise poll for the whole page lifetime; the
  // event listener below still recovers a late load without an endless timer.
  const timeout = setTimeout(() => clearInterval(interval), POLL_TIMEOUT_MS);

  const unsubscribe = onConsentChange(resolve);

  function cleanup() {
    clearInterval(interval);
    clearTimeout(timeout);
    unsubscribe();
  }

  return cleanup;
};

const useGeoState = (): string => useSyncExternalStore(subscribeGeoState, readGeoState, () => '');

const isOptOutState = (state: string): boolean =>
  ALLOWED_STATES.includes(state.toUpperCase() as AllowedState);

export { isOptOutState, useGeoState };
